import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'services/login/hooks';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import {
  useAddProjectCommentMutation,
  useDeleteProjectCommentMutation,
  useGetProjectCommentsQuery,
  useGetProjectEventsQuery,
  useGetProjectsQuery,
  usePatchProjectCommentMutation,
} from 'services/projects/api';
import { Project } from 'services/projects/types';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';
import Discussion from 'components/comments/Discussion';
import { summarizeProjectEvent } from './formatProjectHistoryEvent';

interface ProjectDiscussionProps {
  project: Project;
}

const ProjectDiscussion = ({
  project,
}: ProjectDiscussionProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, user);
  const { data: comments, isLoading: commentsLoading } = useGetProjectCommentsQuery(project.id, {
    skip: !project.id,
  });
  const { data: events, isLoading: eventsLoading } = useGetProjectEventsQuery(project.id, {
    skip: !project.id,
  });
  const { data: projects } = useGetProjectsQuery();
  const [addComment] = useAddProjectCommentMutation();
  const [patchComment] = usePatchProjectCommentMutation();
  const [deleteComment] = useDeleteProjectCommentMutation();

  return (
    <Discussion
      comments={comments}
      currentUserId={user?.id}
      isLoading={commentsLoading || eventsLoading}
      onCreate={comment => addComment({ comment, projectId: project.id }).unwrap()}
      onDelete={id => deleteComment({ id, projectId: project.id }).unwrap()}
      onUpdate={(id, comment) => patchComment({
        comment,
        id,
        projectId: project.id,
      }).unwrap()}
      updates={(events ?? []).map(event => ({
        createdAt: event.createdAt,
        id: event.id,
        summary: summarizeProjectEvent(event, t, {
          projectName: projectId => projects?.find(item => item.id === projectId)?.name,
          userName: userId => {
            const match = users.find(person => person.id === userId);
            return match ? assigneeDisplayName(match) : undefined;
          },
        }),
      }))}
    />
  );
};

export default ProjectDiscussion;
